import React, { useEffect, useRef, useState } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { getUserPrivileges, getUserRole } from "../../utils/auth";
import { CONVERTED_POLICY_PRIVILEGES } from "../../utils/convertedPolicyPrivileges";
import {
  MANAGE_POLICY_PRIVILEGES,
  claimAccessPrivileges,
  endorsementAccessPrivileges
} from "../../utils/managePolicyPrivileges";
import {
  clientManagementAccessPrivileges,
  corporateClientAccessPrivileges,
  retailClientAccessPrivileges
} from "../../utils/clientManagementPrivileges";
import {
  EMPLOYEE_MANAGEMENT_PRIVILEGES,
  employeeManagementAccessPrivileges
} from "../../utils/employeeManagementPrivileges";
import {
  PRODUCT_MASTER_PRIVILEGES,
  productMasterAccessPrivileges
} from "../../utils/productMasterPrivileges";
import { POLICY_TRANSACTION_PRIVILEGES } from "../../utils/policyTransactionPrivileges";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";


import {
  FaHome,
  FaUser,
  FaUsers,
  FaBuilding,
  FaFileMedical,
  FaExchangeAlt,
  FaUserCheck,
  FaFileSignature,
  FaFileInvoiceDollar,
  FaRedoAlt,
  FaBook,
  FaRupeeSign,
  FaBalanceScale,
  FaRegFileAlt,
  FaChartBar,
  FaCalculator,
  FaCogs,
  FaUniversity,
  FaClipboardList,
  FaLayerGroup,
  FaTags,
  FaCog,
  FaHistory,
  FaEnvelope,
  FaPlus,
  FaUserPlus,
  FaUserShield,
  FaUserFriends,
  FaBookOpen,
  FaAddressBook,
  FaLocationArrow,
  FaEmpire,
  FaRobot
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import "./Sidebar.css";

const pathParentSubmenuMap = {
  "/dashboard": [],

  "/prospect/retail-prospect": ["prospects"],
  "/prospect/retail-prospect-manage": ["prospects", "manageProspects"],
  "/prospect/corporate-prospect-manage": ["prospects", "manageProspects"],

  "/add-policy": ["transactions", "policyTransactions"],
  "/draft-policies": ["transactions", "policyTransactions"],
  "/retail/converted-policy": ["transactions", "convertedPolicy"],
  "/corporate/converted-policy": ["transactions", "convertedPolicy"],

  "/endorsements/add": ["managePolicy"],
  "/claims": ["managePolicy"],
  "/renewals": ["managePolicy"],

  "/retail/reports/premium-register": ["reports"],
  "/reports/business-statements": ["reports"],
  "/retail/reports/irdai-reports": ["reports"],
  "/finance/manage-invoices": ["finance"],
  "/finance/commission-ledger": ["finance"],
  "/reports/daily-summary": ["reports"],

  "/register": ["systemSettings", "employees"],
  "/admin/roles": ["systemSettings", "employees"],
  "/users/manage": ["systemSettings", "employees"],
  "/admin/audit-logs": ["systemSettings", "employees"],
  "/retail/client-list": ["systemSettings", "clientManagement"],
  "/corporate/client-list": ["systemSettings", "clientManagement"],

  "/insurer-master": ["systemSettings", "productMaster"],
  "/insurer-branch-master": ["systemSettings", "productMaster"],
  "/policy-master": ["systemSettings", "productMaster"],
  "/class-of-policy-master": ["systemSettings", "productMaster"],
  "/category-master": ["systemSettings", "productMaster"],
  "/admin/privileges": ["systemSettings"],
  "/admin/assistant-knowledge": ["systemSettings"],

  "/geo-location": ["geoCapture"],
  "/location-list": ["geoCapture"],

  "/add-pos": ["pos"],
  "/view-pos": ["pos"],
  "/pos-list": ["pos"]
};

const DEFAULT_SIDEBAR_WIDTH = 250;
const MIN_SIDEBAR_WIDTH = 220;
const MAX_SIDEBAR_WIDTH = 440;
const COLLAPSED_SIDEBAR_WIDTH = 80;
const SIDEBAR_WIDTH_STORAGE_KEY = "sidebarWidth";

const clampSidebarWidth = width =>
  Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));

const MySidebar = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarContainerRef = useRef(null);

  const [activeItem, setActiveItem] = useState("");
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedValue = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    const savedWidth = Number(savedValue);
    return savedValue !== null && Number.isFinite(savedWidth)
      ? clampSidebarWidth(savedWidth)
      : DEFAULT_SIDEBAR_WIDTH;
  });
  const userPrivileges = getUserPrivileges();
  const isAdmin = getUserRole() === "admin";

  const hasPrivilege = (key) => isAdmin || userPrivileges.includes(key);

  const RETAIL_PROSPECT_PRIVILEGES = [
    "manageProspects.retail.active.view",
    "manageProspects.retail.lost.view"
  ];

  const canViewRetailProspects = isAdmin || RETAIL_PROSPECT_PRIVILEGES.some(p =>
    userPrivileges.includes(p)
  );

  const CORPORATE_PROSPECT_PRIVILEGES = [
    "manageProspects.corporate.active.view",
    "manageProspects.corporate.lost.view"
  ];

  const canViewCorporateProspects = isAdmin || CORPORATE_PROSPECT_PRIVILEGES.some(p =>
    userPrivileges.includes(p)
  );

  const canViewManageProspects =
    isAdmin ||
    canViewRetailProspects || canViewCorporateProspects;

  const canViewRetailPolicy = hasPrivilege(CONVERTED_POLICY_PRIVILEGES.retail.view);
  const canViewCorporatePolicy = hasPrivilege(CONVERTED_POLICY_PRIVILEGES.corporate.view);

  const canViewConvertedPolicy =
    isAdmin ||
    canViewRetailPolicy || canViewCorporatePolicy;

  const canAccessAddPolicy = hasPrivilege(POLICY_TRANSACTION_PRIVILEGES.addPolicy.access);
  const canAccessDraftPolicies = hasPrivilege(POLICY_TRANSACTION_PRIVILEGES.draftPolicies.access);
  const canViewPolicyTransactions = canAccessAddPolicy || canAccessDraftPolicies;
  const canViewTransactions = canViewPolicyTransactions || canViewConvertedPolicy;

  const MANAGE_POLICY_PRIVILEGE_KEYS = [
    ...endorsementAccessPrivileges,
    ...claimAccessPrivileges,
    MANAGE_POLICY_PRIVILEGES.renewals.process
  ];

  const canViewEndorsements = isAdmin || endorsementAccessPrivileges.some(p =>
    userPrivileges.includes(p)
  );
  const canViewClaims = isAdmin || claimAccessPrivileges.some(p =>
    userPrivileges.includes(p)
  );
  const canViewRenewals = hasPrivilege(MANAGE_POLICY_PRIVILEGES.renewals.process);

  const canViewManagePolicy = isAdmin || MANAGE_POLICY_PRIVILEGE_KEYS.some(p =>
    userPrivileges.includes(p)
  );

  const REPORT_PRIVILEGES = [
    "reports.premiumRegister",
    "reports.businessStatements",
    "reports.irdiaReports",
    "reports.dailySummary"
  ];

  const canViewReports = isAdmin || REPORT_PRIVILEGES.some(p =>
    userPrivileges.includes(p)
  );

  const FINANCE_PRIVILEGES = [
    "finance.invoiceManagement",
    "finance.commissionManagement"
  ];

  const canViewFinance = isAdmin || FINANCE_PRIVILEGES.some(p =>
    userPrivileges.includes(p)
  );

  const EMPLOYEE_REGISTRATION_PRIVILEGES = [
    ...employeeManagementAccessPrivileges,
    "systemSettings.auditLogs"
  ];

  const canViewEmployeeCreatUser = isAdmin || EMPLOYEE_REGISTRATION_PRIVILEGES.some(p =>
    userPrivileges.includes(p)
  );


  const canViewRetailClients = isAdmin || retailClientAccessPrivileges.some(p =>
    userPrivileges.includes(p)
  );
  const canViewCorporateClients = isAdmin || corporateClientAccessPrivileges.some(p =>
    userPrivileges.includes(p)
  );
  const canViewClientManagement = isAdmin || clientManagementAccessPrivileges.some(p =>
    userPrivileges.includes(p)
  );

  // check if user has at least one Product Master privilege
  const canViewProductMaster = isAdmin || productMasterAccessPrivileges.some(p =>
    userPrivileges.includes(p)
  );
  const canViewPrivilegeMaster = hasPrivilege("privilege.manage");
  const canViewSystemSettings =
    isAdmin ||
    canViewEmployeeCreatUser ||
    canViewClientManagement ||
    canViewProductMaster ||
    canViewPrivilegeMaster;

  useEffect(() => {
    const path = location.pathname;
    setActiveItem(path);

    const parents = pathParentSubmenuMap[path] || [];
    setOpenSubmenus(prev => {
      const updated = { ...prev };
      parents.forEach(key => (updated[key] = true));
      return updated;
    });
  }, [location.pathname]);

  const toggleSubmenu = key => {
    setOpenSubmenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActivePath = path => activeItem === path;

  const navItemProps = path => ({
    active: isActivePath(path),
    className: isActivePath(path) ? "active-menu-item" : "",
    onClick: () => goTo(path)
  });

  const goTo = path => navigate(path);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handlePointerMove = event => {
      const left = sidebarContainerRef.current?.getBoundingClientRect().left || 0;
      setSidebarWidth(clampSidebarWidth(event.clientX - left));
    };

    const stopResizing = () => setIsResizing(false);
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopResizing);
    window.addEventListener("pointercancel", stopResizing);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResizing);
      window.removeEventListener("pointercancel", stopResizing);
    };
  }, [isResizing]);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
  }, [sidebarWidth]);

  const handleResizeKeyDown = event => {
    const resizeSteps = {
      ArrowLeft: -16,
      ArrowRight: 16
    };

    if (event.key === "Home") {
      event.preventDefault();
      setSidebarWidth(MIN_SIDEBAR_WIDTH);
    } else if (event.key === "End") {
      event.preventDefault();
      setSidebarWidth(MAX_SIDEBAR_WIDTH);
    } else if (resizeSteps[event.key]) {
      event.preventDefault();
      setSidebarWidth(current => clampSidebarWidth(current + resizeSteps[event.key]));
    }
  };

  return (
    <div
      ref={sidebarContainerRef}
      className={`sidebar-main-container${isResizing ? " is-resizing" : ""}`}
    >
      <Sidebar
        collapsed={collapsed}
        width={`${sidebarWidth}px`}
        collapsedWidth={`${COLLAPSED_SIDEBAR_WIDTH}px`}
      >
        <div className="sidebar-scroll-area">
          <button
              className="toggle-btn"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <FaAngleRight /> : <FaAngleLeft />}
            </button>
          <Menu>
            


            {/* Dashboard */}
            <MenuItem
              icon={<FaHome />}
              {...navItemProps("/dashboard")}
            >
              Dashboard
            </MenuItem>

            {/* Prospects */}
            <SubMenu
              icon={<FaUser />}
              label="Prospects"
              open={openSubmenus.prospects}
              onClick={() => toggleSubmenu("prospects")}
            >
              <MenuItem
                icon={<FaPlus />}
                {...navItemProps("/prospect/retail-prospect")}
              >
                Add Prospect
              </MenuItem>

              {canViewManageProspects && (
                <SubMenu
                  icon={<FaUsers />}
                  label="Manage Prospects"
                  open={openSubmenus.manageProspects}
                  onClick={() => toggleSubmenu("manageProspects")}
                >
                  {canViewRetailProspects && (
                    <MenuItem
                      icon={<FaUser />}
                      {...navItemProps("/prospect/retail-prospect-manage")}
                    >
                      Retail
                    </MenuItem>
                  )}
                  {canViewCorporateProspects && (
                    <MenuItem
                      icon={<FaBuilding />}
                      {...navItemProps("/prospect/corporate-prospect-manage")}
                    >
                      Corporate
                    </MenuItem>
                  )}
                </SubMenu>
              )}
            </SubMenu>

            {/* Transactions */}
            {canViewTransactions && <SubMenu
              icon={<FaEnvelope />}
              label="Transactions"
              open={openSubmenus.transactions}
              onClick={() => toggleSubmenu("transactions")}
            >
              {canViewPolicyTransactions && <SubMenu
                icon={<FaFileMedical />}
                label="Policy"
                open={openSubmenus.policyTransactions}
                onClick={() => toggleSubmenu("policyTransactions")}
              >
                {canAccessAddPolicy && <MenuItem
                  icon={<FaPlus />}
                  {...navItemProps("/add-policy")}
                >
                  Add Policy
                </MenuItem>}
                {canAccessDraftPolicies && <MenuItem
                  icon={<FaBook />}
                  {...navItemProps("/draft-policies")}
                >
                  Draft Policies
                </MenuItem>}
              </SubMenu>}
              {canViewConvertedPolicy && (
                <SubMenu
                  icon={<FaExchangeAlt />}
                  label="Converted Policy"
                  open={openSubmenus.convertedPolicy}
                  onClick={() => toggleSubmenu("convertedPolicy")}
                >
                  {canViewRetailPolicy && (
                    <MenuItem icon={<FaUser />} {...navItemProps("/retail/converted-policy")}>
                      Retail
                    </MenuItem>
                  )}
                  {canViewCorporatePolicy && (
                    <MenuItem icon={<FaBuilding />} {...navItemProps("/corporate/converted-policy")}>
                      Corporate
                    </MenuItem>
                  )}
                </SubMenu>
              )}
            </SubMenu>}

            {/* Manage Policy */}
            {canViewManagePolicy && (
              <SubMenu
                icon={<FaAddressBook />}
                label="Manage Policy"
                open={openSubmenus.managePolicy}
                onClick={() => toggleSubmenu("managePolicy")}
              >
                {canViewEndorsements && (
                  <MenuItem icon={<FaFileSignature />} {...navItemProps("/endorsements/add")}>Endorsement</MenuItem>
                )}
                {canViewClaims && (
                  <MenuItem icon={<FaFileInvoiceDollar />} {...navItemProps("/claims")}>Claims</MenuItem>
                )}
                {canViewRenewals && (
                  <MenuItem
                    icon={<FaRedoAlt />}
                    {...navItemProps("/renewals")}
                  >
                    Renewals
                  </MenuItem>
                )}
              </SubMenu>
            )}

            {/* Reports */}
            {canViewReports && (
              <SubMenu
                icon={<FaBookOpen />}
                label="Reports"
                open={openSubmenus.reports}
                onClick={() => toggleSubmenu("reports")}
              >
                {hasPrivilege("reports.premiumRegister") && (
                  <MenuItem icon={<FaRupeeSign />} {...navItemProps("/retail/reports/premium-register")}>
                    Premium Register
                  </MenuItem>
                )}
                {hasPrivilege("reports.businessStatements") && (
                  <MenuItem icon={<FaRegFileAlt />} {...navItemProps("/reports/business-statements")}>
                    Business Statements
                  </MenuItem>
                )}
                {hasPrivilege("reports.irdiaReports") && (
                  <MenuItem icon={<FaBalanceScale />} {...navItemProps("/retail/reports/irdai-reports")}>
                    IRDAI Reports
                  </MenuItem>
                )}
                {hasPrivilege("reports.dailySummary") && (
                  <MenuItem
                    icon={<FaChartBar />}
                    {...navItemProps("/reports/daily-summary")}
                  >
                    Daily Summary
                  </MenuItem>
                )}
              </SubMenu>
            )}

            {/* Finance */}
            {canViewFinance && (
              <SubMenu
                icon={<FaUniversity />}
                label="Finance"
                open={openSubmenus.finance}
                onClick={() => toggleSubmenu("finance")}
              >
                {hasPrivilege("finance.invoiceManagement") && (
                  <MenuItem icon={<FaRegFileAlt />} {...navItemProps("/finance/manage-invoices")}>
                    Invoice Management
                  </MenuItem>
                )}
                {hasPrivilege("finance.commissionManagement") && (
                  <MenuItem icon={<FaCalculator />} {...navItemProps("/finance/commission-ledger")}>
                    Commission Management
                  </MenuItem>
                )}
              </SubMenu>
            )}

            {/* System Settings */}
            {canViewSystemSettings && (
            <SubMenu
              icon={<FaCog />}
              label="System Settings"
              open={openSubmenus.systemSettings}
              onClick={() => toggleSubmenu("systemSettings")}
            >
              {canViewEmployeeCreatUser && (
                <SubMenu
                  icon={<FaUserFriends />}
                  label="Employees"
                  open={openSubmenus.employees}
                  onClick={() => toggleSubmenu("employees")}
                >
                  {hasPrivilege(EMPLOYEE_MANAGEMENT_PRIVILEGES.userCreation.create) && (
                    <MenuItem icon={<FaUserPlus />} {...navItemProps("/register")}>
                      Create User
                    </MenuItem>
                  )}
                  {(isAdmin || EMPLOYEE_MANAGEMENT_PRIVILEGES.roles.access.some(p => userPrivileges.includes(p))) && (
                    <MenuItem
                      icon={<FaUserShield />}
                      {...navItemProps("/admin/roles")}
                    >
                      Roles
                    </MenuItem>
                  )}
                  {(isAdmin || EMPLOYEE_MANAGEMENT_PRIVILEGES.users.access.some(p => userPrivileges.includes(p))) && (
                    <MenuItem
                      icon={<FaUserCheck />}
                      {...navItemProps("/users/manage")}
                    >
                      Manage Users
                    </MenuItem>
                  )}
                  {hasPrivilege("systemSettings.auditLogs") && (
                    <MenuItem
                      icon={<FaHistory />}
                      {...navItemProps("/admin/audit-logs")}
                    >
                      Audit Logs
                    </MenuItem>
                  )}
                </SubMenu>
              )}

              {canViewClientManagement && (
                <SubMenu
                  icon={<FaUsers />}
                  label="Client Management"
                  open={openSubmenus.clientManagement}
                  onClick={() => toggleSubmenu("clientManagement")}
                >
                  {canViewRetailClients && (
                    <MenuItem
                      icon={<FaUser />}
                      {...navItemProps("/retail/client-list")}
                    >
                      Retail Clients
                    </MenuItem>
                  )}
                  {canViewCorporateClients && (
                    <MenuItem
                      icon={<FaBuilding />}
                      {...navItemProps("/corporate/client-list")}
                    >
                      Corporate Clients
                    </MenuItem>
                  )}
                </SubMenu>
              )}

              {/* ✅ Product Master */}
              {canViewProductMaster && (
                <SubMenu
                  icon={<FaCogs />}
                  label="Product Master"
                  open={openSubmenus.productMaster}
                  onClick={() => toggleSubmenu("productMaster")}
                >
                  {(isAdmin || PRODUCT_MASTER_PRIVILEGES.insurer.access.some(hasPrivilege)) && (
                    <MenuItem icon={<FaUniversity />} {...navItemProps("/insurer-master")}>
                      Insurer Master
                    </MenuItem>
                  )}
                  {(isAdmin || PRODUCT_MASTER_PRIVILEGES.branch.access.some(hasPrivilege)) && (
                    <MenuItem icon={<FaUniversity />} {...navItemProps("/insurer-branch-master")}>
                      Insurer Branch Master
                    </MenuItem>
                  )}
                  {(isAdmin || PRODUCT_MASTER_PRIVILEGES.policy.access.some(hasPrivilege)) && (
                    <MenuItem icon={<FaClipboardList />} {...navItemProps("/policy-master")}>
                      Policy Master
                    </MenuItem>
                  )}
                  {(isAdmin || PRODUCT_MASTER_PRIVILEGES.classOfPolicy.access.some(hasPrivilege)) && (
                    <MenuItem icon={<FaLayerGroup />} {...navItemProps("/class-of-policy-master")}>
                      Class of Policy
                    </MenuItem>
                  )}
                  {(isAdmin || PRODUCT_MASTER_PRIVILEGES.category.access.some(hasPrivilege)) && (
                    <MenuItem icon={<FaTags />} {...navItemProps("/category-master")}>
                      Category
                    </MenuItem>
                  )}
                </SubMenu>
              )}

              {canViewPrivilegeMaster && (
                <MenuItem
                  icon={<FaUniversity />}
                  {...navItemProps("/admin/privileges")}
                >
                  Privilege Master
                </MenuItem>
              )}
              {canViewPrivilegeMaster && (
                <MenuItem
                  icon={<FaRobot />}
                  {...navItemProps("/admin/assistant-knowledge")}
                >
                  AI Knowledge
                </MenuItem>
              )}

            </SubMenu>
            )}

            {/* Geo Capture */}
            <SubMenu
              icon={<FaLocationArrow />}
              label="Geo Capture"
              open={openSubmenus.geoCapture}
              onClick={() => toggleSubmenu("geoCapture")}
            >
              <MenuItem {...navItemProps("/geo-location")}>Photo Capture</MenuItem>
              <MenuItem {...navItemProps("/location-list")}>Location Data</MenuItem>
            </SubMenu>

            {/* POS */}
            <SubMenu
              icon={<FaEmpire />}
              label="POS"
              open={openSubmenus.pos}
              onClick={() => toggleSubmenu("pos")}
            >
              <MenuItem {...navItemProps("/add-pos")}>Add POS</MenuItem>
              <MenuItem {...navItemProps("/view-pos")}>POS Details</MenuItem>
              <MenuItem {...navItemProps("/pos-list")}>POS Advisors</MenuItem>
            </SubMenu>
          </Menu>
        </div>
      </Sidebar>
      {!collapsed && (
        <div
          className="sidebar-resize-handle"
          role="separator"
          aria-label="Resize sidebar"
          aria-orientation="vertical"
          aria-valuemin={MIN_SIDEBAR_WIDTH}
          aria-valuemax={MAX_SIDEBAR_WIDTH}
          aria-valuenow={sidebarWidth}
          tabIndex={0}
          onPointerDown={event => {
            event.preventDefault();
            setIsResizing(true);
          }}
          onKeyDown={handleResizeKeyDown}
          title="Drag to resize sidebar"
        />
      )}
    </div>
  );
};

export default MySidebar;
